import { choice } from './utils';
import { decode } from 'he';
import striptags from 'striptags';

/**
 * String utilities
 *
 */

/**
 * Truncates a string to a given length, and adds ellipses if it was truncated.
 *
 * @param str The string to truncate
 * @param maxLength The maximum lenght of the resulting string, including ellipses
 * @returns the original string, truncated to the `maxLength`
 */
export function truncate(str: string, maxLength: number) {
  if (maxLength < 1) return '';
  if (str.length <= maxLength) return str;

  return str.substring(0, maxLength - 3) + '...';
}

/**
 * Truncates a string to a given length, respecting word boundaries
 *
 */
const WORD_BOUNDARY_CHARS = ' ,.!?-:;)%#';

export function truncateWords(str: string, maxLength: number) {
  if (!(str as any)) return '';
  if (maxLength < 1) return '';
  if (str.length <= maxLength) return str;

  let endChar = maxLength - 3; // start counting backwards from the max length - ellipses
  while (!WORD_BOUNDARY_CHARS.includes(str[endChar]) && endChar > 0) endChar--;
  return str.substring(0, endChar) + '...';
}

/**
 * Capitalizes a string
 * @param str the string to capitalize
 */
export function capitalize(str: string) {
  if (str === '') return str;
  return str[0].toUpperCase() + str.substring(1).toLowerCase();
}

const RANDOM_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

export function randomString(size: number): string {
  let result = '';

  if (size < 1) return result;

  for (let i = 0; i < size; i++) {
    result += choice(RANDOM_CHARS.split(''));
  }
  return result;
}

/**
 * Normalizes a string by turning it to lowercase and removing trailing
 * spaces. This is useful when we want to compare keys (e.g. plugin names) and
 * they come from user input.
 *
 */
export function normalize(str: string): string {
  return str.toLowerCase().trim();
}

/**
 * Returns true if the argument is a string and it is not empty
 */
export function isNonEmptyString(str: any): boolean {
  return !!(str && typeof str === 'string' && str.trim() !== '');
}

// Converts aStringLikeThis to A String Like This
// Supports acronyms
export function camelToSentenceCase(text: string) {
  let result = text.replace(/([a-z])([A-Z])/g, '$1 $2');
  result = result.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

/**
 * Returns a slug version of a string
 */
export function createSlug(text: string): string {
  // Normalize text to NFD (Normalization Form Decomposition)
  // for example replace "é" with "e"
  const normalizedText = text.normalize('NFD');

  // Delete diacritics characters from the Unicode "Mark" types
  const withoutDiacritics = normalizedText.replace(/[\u0300-\u036f]/g, '');

  // Delete all non a-Z or 0-9 chars and preserve spaces
  const withoutSpecialChars = withoutDiacritics.replace(/[^a-zA-Z0-9\s]/g, '');

  // Replace spaces by hyphens
  const withHyphens = withoutSpecialChars.replace(/\s+/g, '-');

  const slug = withHyphens.toLowerCase();

  return slug;
}

// Converts a size in bytes to a human readable string
const KB = 1024.0;
const MB = KB * KB;
const GB = MB * KB;
const TB = GB * KB;
export function sizeToString(size: number) {
  if (size < KB) return `${+size.toFixed(2)} bytes`;
  if (size < MB) return `${+(size / KB).toFixed(2)} KB`;
  if (size < GB) return `${+(size / MB).toFixed(2)} MB`;
  if (size < TB) return `${+(size / GB).toFixed(2)} GB`;

  return `${+(size / TB).toFixed(2)} TB`;
}

// Takes a text with HTML and returns just the text content
export function stripHTML(html: string): string {
  const decoded = decode(html);
  return striptags(decoded);
}
